import { Box, Button, Flex, Table, Tbody, Td, Text, Tr } from "@chakra-ui/react"
import UserAvatar from "@pathwright/ux/src/components/avatar/UserAvatar"
import UserAvatarStack from "@pathwright/ux/src/components/avatar/UserAvatarStack"
import { AvatarUser } from "@pathwright/ux/src/components/avatar/types"
import Link, { LinkProps } from "@pathwright/ux/src/components/link/Link"
import { motion } from "framer-motion"
import { ReactNode } from "react"
import { usePathwrightIntercomGuidanceTeamMembersQuery } from "../../api/generated"
import { useIntercomContext } from "../../intercom/IntercomProvider"
import { useGetNewResource } from "../../path/create/CreateOrSelectResource"
import { usePathCreatorContext } from "../../path/create/PathCreatorContext"
import Pathicon from "../../pathicon/Pathicon"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import Tip, { TipProps } from "../../tips/Tip"

function usePathwrightIntercomTeamMemberAvatarUsers() {
  const { usingPathwrightIntercom } = useIntercomContext()
  const pathwrightIntercomGuidanceTeamMembersQuery =
    usePathwrightIntercomGuidanceTeamMembersQuery({
      skip: !usingPathwrightIntercom
    })
  const pwIntercomGuidanceTeamMembers =
    pathwrightIntercomGuidanceTeamMembersQuery.data?.integrations?.intercom
      ?.team || []

  // Map to AvatarUser[] to be used in UserAvatarStack.
  const avatarUsers: AvatarUser[] = pwIntercomGuidanceTeamMembers.map(
    (member) => ({
      id: member.id,
      first_name: member.name.split(/ +/)[0],
      last_name: member.name.split(/ +/).slice(1).join(" "),
      full_name: member.name,
      profile: {
        image: member.avatar
      }
    })
  )

  return avatarUsers
}

// For formatting each row in the tip table, each row having 3 columns
// and behaving like a link/button.
function TipRow({
  columns,
  ...linkProps
}: {
  columns: [ReactNode, ReactNode, ReactNode]
} & LinkProps) {
  return (
    <Tr
      sx={{
        "&:last-of-type td": {
          // No border for tds in last row.
          borderBottomWidth: "0px"
        }
      }}
      p={0}
      // NOTE: border-radius doesn't apply to table rows.
      borderRadius="md"
      bg="transparent"
      _hover={{ bg: "whiteAlpha.100" }}
    >
      <Td
        pos="relative"
        py={0}
        px={2}
        borderColor="whiteAlpha.400"
        _groupHover={{ borderColor: "transparent" }}
      >
        <Flex justifyContent="center">{columns[0]}</Flex>
        <Link as="a" pos="absolute" inset={0} p={0} {...linkProps} />
      </Td>
      {/* Allowing middle text column to be responsible for padding the row top/bottom,
      making space for the demo avatar to grow without increasing the row height (within margin of error). */}
      <Td
        pos="relative"
        py={4}
        px={2}
        borderColor="whiteAlpha.400"
        _groupHover={{ borderColor: "transparent" }}
      >
        <Text color="inherit" m={0}>
          {columns[1]}
        </Text>
        <Link as="a" pos="absolute" inset={0} p={0} {...linkProps} />
      </Td>
      <Td
        pos="relative"
        py={0}
        px={2}
        borderColor="whiteAlpha.400"
        _groupHover={{ borderColor: "transparent" }}
      >
        {columns[2]}
        <Link as="a" pos="absolute" inset={0} p={0} {...linkProps} />
      </Td>
    </Tr>
  )
}

// Only intended for Pathwright Intercom.
function PathwrightIntercomTeamMembers() {
  const avatarUsers = usePathwrightIntercomTeamMemberAvatarUsers()

  return avatarUsers.length ? (
    <UserAvatarStack users={avatarUsers} max={avatarUsers.length} size="xs" />
  ) : null
}

// For activating the "Add Path" flow.
function useAddPath() {
  const { setInitialValue } = usePathCreatorContext()
  const { getNewResource } = useGetNewResource()
  async function addPath() {
    setInitialValue(await getNewResource())
  }

  return addPath
}

function HomeWelcomeDesignerBlankSlateTip(props: Partial<TipProps>) {
  const { me, school } = usePathwrightContext()
  const avatarUsers = usePathwrightIntercomTeamMemberAvatarUsers()
  // Grab Michelle's avatar.
  const michelleAvatarUser = avatarUsers.find(
    (user) => user.first_name === "Michelle"
  )
  const addPath = useAddPath()

  return (
    <Tip
      tip={{
        id: "home-welcome-designer-blank-slate",
        title: `Welcome, ${me.first_name}!`,
        content: (
          <Box>
            <Text color="inherit" mt={0}>
              This Home screen helps you keep track of all your learning Paths.
              Here are three ways to get started:
            </Text>
            <Box mt={4}>
              {avatarUsers.length ? (
                <motion.div
                  key="content"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  <Table className="group">
                    <Tbody>
                      <TipRow
                        columns={[
                          <Pathicon icon="plus-circle-fill" size="1.6em" />,
                          "Get started right away by adding a Path.",
                          <Pathicon icon="chevron-right" size="1.2em" />
                        ]}
                        onClick={addPath}
                      />
                      <TipRow
                        columns={[
                          <PathwrightIntercomTeamMembers />,
                          "Ask any question and get an answer from an expert Path Guide or our Help Center.",
                          <Pathicon icon="chevron-right" size="1.2em" />
                        ]}
                        onClick={() => {
                          window.Intercom?.("showNewMessage")
                        }}
                      />
                      {!!michelleAvatarUser && (
                        <TipRow
                          columns={[
                            <UserAvatar user={michelleAvatarUser} size="sm" />,
                            "Schedule a free, personal demo and Q&A time with Michelle.",
                            <Pathicon icon="chevron-right" size="1.2em" />
                          ]}
                          href={`https://calendly.com/michellejjohnson/one-on-one?utm_source=platform&utm_medium=website&utm_campaign=home_welcome_tip&utm_content=school-${school.subdomain}-${school.id}`}
                          target="_blank"
                          rel="noopener"
                        />
                      )}
                    </Tbody>
                  </Table>
                </motion.div>
              ) : (
                <Text color="inherit">
                  To get started,{" "}
                  <Button
                    variant="link"
                    bg="transparent"
                    color="inherit"
                    cursor="pointer"
                    _hover={{
                      color: "inherit"
                    }}
                    onClick={addPath}
                  >
                    add a Path
                  </Button>
                  .
                </Text>
              )}
            </Box>
          </Box>
        )
      }}
      icon="lightbulb-on"
      layout="dismissable"
      colorScheme="dark"
      {...props}
    />
  )
}

export default HomeWelcomeDesignerBlankSlateTip
